import { isEmpty, isNil } from "lodash-es";
import { Moment } from "moment";
import moment from "moment-timezone";
import { DateRange } from "react-big-calendar";

import { UnitOfTime } from "@/core/api/generated/v1";

import { DATETIME_FORMATS } from "../constants/common";
import { DurationParts, DurationRepresentation } from "../ts/duration";
import { TimeSpan } from "../ts/timespan";

const iso8601DurationRegex = /^P(\d+Y)?(\d+M)?(\d+D)?(T(\d+H)?(\d+M)?(\d+S)?)?$/;
const dotNetTimeSpamRegex =
  /^(?<days>\d+(\.|,|:){1})?(?<time>\d+:\d+:\d+)(?<milliseconds>(\.|,){1}\d+)?$/;

// See: https://momentjs.com/docs/#/durations/humanize/
const defaultMomentArgThresholdOpts: moment.argThresholdOpts = {
  // moment defaults:
  // ss: 44,  // "a few seconds" threshold (must be set after 's'; represents the upper limit for "few seconds" before switching to seconds)
  // s: 45,   // seconds → minutes (if ≥45 seconds, use "minutes")
  // m: 45,  // minutes → hours (if ≥45 minutes, use "hours")
  // h: 22,  // hours → days (if ≥22 hours, use "days")
  // d: 26,  // days → months (if ≥26 days, use "months")
  // w: null,  // weeks → months (disabled by default; not used in humanization)
  // M: 11,  // months → years (if ≥11 months, use "years")

  ss: 49,
  s: 50,
  m: 60,
  h: 24,
  d: 28,
  w: null, // don't show in weeks, only days
  M: 12,
};

export interface DateRangeDurationHumanizeOptions {
  /** By default, the return string is describing a duration 'a month' (suffix-less). If you want an oriented duration 'in a month', 'a month ago' (with suffix), pass in true.
   * @default false
   */
  isSuffix?: boolean;
  /** Max unit of time to display the duration. */
  maxUnit?: UnitOfTime;
}

export interface DurationHumanizeOptions {
  /** By default, the return string is describing a duration 'a month' (suffix-less). If you want an oriented duration 'in a month', 'a month ago' (with suffix), pass in true.
   * @default false
   */
  isSuffix?: boolean;
  /** Max unit of time to display the duration. */
  maxUnit?: UnitOfTime;
}

export class DatetimeHelper {
  /** Returns min date value. */
  public static get minDate(): Date {
    return new Date(1970, 0, 1, 0, 0, 0, 0); // new Date(-8640000000000000)
  }

  /** Returns min date value. */
  public static get minDateAsMoment(): Moment {
    return moment(this.minDate);
  }

  /** Returns max date value. */
  public static get maxDate(): Date {
    return new Date(275760, 8, 13, 0, 0, 0, 0); // new Date(8640000000000000)
  }

  /** Returns max date value. */
  public static get maxDateAsMoment(): Moment {
    return moment(this.maxDate);
  }

  /** Formats datetime. */
  public static formatDatetime(date?: string | Moment | null): string {
    if (!date) {
      return "";
    }
    return moment(date).format(DATETIME_FORMATS.DISPLAY_DATETIME);
  }

  public static humanizeDateDayFromNow(date?: string | Moment | null): string {
    if (!date) {
      return "";
    }

    const dateM = moment.utc(date).local().startOf("day");
    const todayDateM = moment().startOf("day");

    const diffDays = todayDateM.diff(dateM, "days");
    const diffYears = todayDateM.diff(dateM, "years");

    if (diffDays === 0) {
      return "Today";
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffYears === 0) {
      return dateM.format("MMMM D");
    } else {
      return dateM.format("MMMM D, YYYY");
    }
  }

  public static getDayRangeInclusive(date: Date = new Date()): DateRange {
    return {
      start: moment(date).startOf("day").toDate(),
      end: moment(date).endOf("day").toDate(),
    };
  }

  public static getDayRangeExclusive(date: Date = new Date()): DateRange {
    return {
      start: moment(date).startOf("day").toDate(),
      end: moment(date).add(1, "day").startOf("day").toDate(),
    };
  }

  public static getMonthRangeInclusive(date: Date = new Date()): DateRange {
    return {
      start: moment(date).startOf("month").toDate(),
      end: moment(date).endOf("month").toDate(),
    };
  }

  public static getMonthRangeExclusive(date: Date = new Date()): DateRange {
    return {
      start: moment(date).startOf("month").toDate(),
      end: moment(date).add(1, "month").startOf("month").toDate(),
    };
  }

  //#region Duration

  // NB:
  // As of 2.1.0, moment supports parsing .NET style time spans. https://momentjs.com/docs/#/durations/
  // As of 2.3.0, moment also supports parsing ISO 8601 durations.

  /** Detects DurationRepresentation from provided value.
   *  E.g. either ISO 8601 duration or .NET TimeSpan.
   */
  public static detectDurationRepresentation(value: string): DurationRepresentation {
    if (this.isISO8601Duration(value)) {
      return DurationRepresentation.ISO8601;
    } else if (this.isDotNetTimeSpan(value)) {
      return DurationRepresentation.DotNetTimeSpan;
    } else {
      throw new Error(`Invalid duration value '${value}'. Representation can't be detected.`);
    }
  }

  /** Checks provided value is ISO 8601 duration string.
   *  E.g. P1Y2M10DT2H30M.
   *  https://en.wikipedia.org/wiki/ISO_8601#Time_intervals
   *  https://momentjs.com/docs/#/durations/as-iso-string/
   */
  public static isISO8601Duration(value: string | null | undefined): boolean {
    return (
      !isNil(value) &&
      value.length > 0 &&
      iso8601DurationRegex.test(value) &&
      value !== "P" &&
      value !== "PT"
    );
  }

  /** Checks provided value is .NET TImeSpan string.
   * E.g. 7.23:59:59.999, 14:30:15, 1:14:30:15,0000000
   *  https://learn.microsoft.com/en-us/dotnet/api/system.timespan.tostring?view=net-7.0
   */
  public static isDotNetTimeSpan(value: string | null | undefined): boolean {
    return !isNil(value) && value.length > 0 && dotNetTimeSpamRegex.test(value);
  }

  public static toDurationString(
    duration: DurationParts,
    representation: DurationRepresentation,
  ): string {
    if (representation === DurationRepresentation.ISO8601) {
      return DatetimeHelper.toISO8601DurationString(duration);
    } else if (representation === DurationRepresentation.DotNetTimeSpan) {
      return DatetimeHelper.toDotNetTimeSpanString(duration);
    } else {
      throw new Error(`Unknown duration representation '${representation}'.`);
    }
  }

  /** E.g. P1Y2M10DT2H30M. */
  public static toISO8601DurationString(
    duration: Partial<Record<UnitOfTime, number | undefined>>,
  ): string {
    // const year = duration[UnitOfTime.Year];

    const dateParts = [
      duration[UnitOfTime.Year] ? `${duration[UnitOfTime.Year]}Y` : null,
      duration[UnitOfTime.Month] ? `${duration[UnitOfTime.Month]}M` : null,
      duration[UnitOfTime.Day] ? `${duration[UnitOfTime.Day]}D` : null,
    ]
      .filter((x) => !!x)
      .map((x) => x!);

    const timeParts = [
      duration[UnitOfTime.Hour] ? `${duration[UnitOfTime.Hour]}H` : null,
      duration[UnitOfTime.Minute] ? `${duration[UnitOfTime.Minute]}M` : null,
      duration[UnitOfTime.Second] ? `${duration[UnitOfTime.Second]}S` : null,
    ]
      .filter((x) => !!x)
      .map((x) => x!);

    return `P${dateParts.join("")}${timeParts.length !== 0 ? `T${timeParts.join("")}` : ""}`;
  }

  /** Always outputs in the similar format: 7.23:59:59.999 (dots and semicolons are used). */
  public static toDotNetTimeSpanString(
    duration: Partial<Record<UnitOfTime, number | undefined>>,
  ): string {
    return TimeSpan.fromDurationInput(duration).toString();
  }

  /** Parses duration string and converts to number in specified units. */
  public static parseDurationValueAsUnitOfTime(
    duration: string | null | undefined,
    unitOfTime: UnitOfTime,
  ): number | null {
    if (isNil(duration) || isEmpty(duration)) {
      return null;
    }

    if (this.isISO8601Duration(duration)) {
      // moment.duration parses both ISO and .NET TimeSpan representations
      // NB: has issues with converting 30 days to months. I.e. 90 days != 3 months.
      const momentDuration = moment.duration(duration);
      return TimeSpan.fromMomentDuration(momentDuration).toNumber(unitOfTime);
    } else if (this.isDotNetTimeSpan(duration)) {
      return TimeSpan.fromDurationValue(duration).toNumber(unitOfTime);
    } else {
      throw new Error(`Unknown duration value '${duration}'.`);
    }
  }

  /** Parses duration string, suggest the most suitable units and converts to number in suggested units. */
  public static parseDurationValueAsSuggestedUnitOfTime(duration: string | null | undefined): {
    duration: number | null;
    unitOfTime: UnitOfTime | null;
  } {
    if (isNil(duration) || isEmpty(duration)) {
      return { duration: null, unitOfTime: null };
    }

    const timeSpan =
      (this.isISO8601Duration(duration)
        ? TimeSpan.fromMomentDuration(moment.duration(duration))
        : undefined) ||
      (this.isDotNetTimeSpan(duration) ? TimeSpan.fromDurationValue(duration) : undefined) ||
      undefined;
    if (!timeSpan) {
      throw new Error(`Unknown duration value '${duration}'.`);
    }

    const unitOfTime = timeSpan.suggestUnitOfTime();

    return { duration: timeSpan.toNumber(unitOfTime), unitOfTime: unitOfTime };
  }

  /** Returns duration in user-friendly format.
   * E.g.: a few seconds, 2 hours, 1 minute, 3 minutes, etc.
   * https://momentjs.com/docs/#/durations/humanize/
   * @param duration - ISO 8601 duration or .NET TimeSpan string */
  public static humanizeMomentDuration(
    duration: Nil<moment.Duration>,
    options?: DurationHumanizeOptions,
  ): string {
    if (!duration) {
      return "";
    }

    const maxUnit = options?.maxUnit ?? UnitOfTime.Year;
    const validUnits: UnitOfTime[] = [
      UnitOfTime.Second,
      UnitOfTime.Minute,
      UnitOfTime.Hour,
      UnitOfTime.Day,
      UnitOfTime.Month,
      UnitOfTime.Year,
    ];
    if (maxUnit && !validUnits.includes(maxUnit)) {
      throw new Error(`Invalid maxUnit. Must be one of: ${validUnits.join(", ")}`);
    }

    if (duration.asMilliseconds() === 0) {
      return "zero";
    }

    const maxThreshold = Number.MAX_SAFE_INTEGER;
    const result = duration.humanize(options?.isSuffix, {
      // here threshold specifies max number of units before switching to higher unit,
      // i.e. if duration >= maxThreshold, then switch to higher unit.
      // e.g. {d: 30}: 29 days -> 29 days, 30 days -> 1 month
      ...defaultMomentArgThresholdOpts,
      ...(maxUnit && {
        s: maxUnit === UnitOfTime.Second ? maxThreshold : defaultMomentArgThresholdOpts.s,
        m: maxUnit === UnitOfTime.Minute ? maxThreshold : defaultMomentArgThresholdOpts.m,
        h: maxUnit === UnitOfTime.Hour ? maxThreshold : defaultMomentArgThresholdOpts.h,
        d: maxUnit === UnitOfTime.Day ? maxThreshold : defaultMomentArgThresholdOpts.d,
        w: null,
        M: maxUnit === UnitOfTime.Month ? maxThreshold : defaultMomentArgThresholdOpts.M,
      }),
    });
    return result;
  }

  /** Returns duration in user-friendly format.
   * E.g.: a few seconds, 2 hours, 1 minute, 3 minutes, etc.
   * https://momentjs.com/docs/#/durations/humanize/
   * @param duration - ISO 8601 duration or .NET TimeSpan string */
  public static humanizeDuration(duration: Nil<string>, options?: DurationHumanizeOptions): string {
    if (!duration) {
      return "";
    }
    const durationM = moment.duration(duration);
    return this.humanizeMomentDuration(durationM, options);
  }

  /** Returns duration (always positive) between two dates in user-friendly format.
   *  E.g.: a few seconds, 2 hours, 1 minute, 3 minutes, etc.
   * https://momentjs.com/docs/#/displaying/from/ */
  public static humanizeDateRangeDurationV0_1(
    start: string | Moment | null | undefined,
    end: string | Moment | null | undefined,
    options?: DateRangeDurationHumanizeOptions,
  ): string {
    if (!start || !end) {
      return "";
    }

    const startM = moment(start);
    const endM = moment(end);

    // order in [start, end)
    const startM2 = startM.isSameOrBefore(endM) ? startM : endM;
    const endM2 = startM.isAfter(endM) ? startM : endM;

    return startM2.to(endM2, options?.isSuffix ? !options.isSuffix : true);
  }

  /** Returns duration (always positive) between two dates in user-friendly format.
   *  E.g.: a few seconds, 2 hours, 1 minute, 3 minutes, etc.
   * https://momentjs.com/docs/#/displaying/from/ */
  public static humanizeDateRangeDuration(
    start: string | Date | Moment | null | undefined,
    end: string | Date | Moment | null | undefined,
    options?: DateRangeDurationHumanizeOptions,
  ): string {
    if (!start || !end) {
      return "";
    }

    const startM = moment(start);
    const endM = moment(end);
    const durationMs = Math.abs(startM.diff(endM, "milliseconds"));
    const durationM = moment.duration(durationMs, "milliseconds");

    return this.humanizeMomentDuration(durationM, options);
  }

  /** Returns duration from now to specified date in user-friendly format.
   *  Supports both past and future date.
   *  E.g.: a few seconds ago, 2 hours ago, in 1 minute, in 3 minutes, etc.
   * https://momentjs.com/docs/#/displaying/from/ */
  public static humanizeDateRangeDurationFromNow(
    date: string | Moment | null | undefined,
    options?: DateRangeDurationHumanizeOptions,
  ): string {
    if (!date) {
      return "";
    }

    const dateM = moment(date);
    const nowM = moment();

    const isNow = dateM.isSame(nowM);
    const isPast = dateM.isBefore(nowM);
    const isFuture = dateM.isAfter(nowM);
    const nowText = options?.isSuffix
      ? "just now"
      : dateM.from(nowM, options?.isSuffix ? !options.isSuffix : true);

    return isNow
      ? nowText
      : isPast
        ? dateM.from(nowM, options?.isSuffix ? !options.isSuffix : true)
        : isFuture
          ? nowM.to(dateM, options?.isSuffix ? !options.isSuffix : true)
          : "unknown";
  }

  //#endregion
}
